import { z } from "zod";
/**
 * Prompts for a page: paste up to five pages, and Crux finds the questions that
 * make the AI engines cite them.
 *
 * **Measured, not modelled, where it matters.** A model writes the questions —
 * that is a proposal, and nothing on this boundary calls it more. Whether a
 * question cited a page is read from the citations of a capture we actually ran,
 * by comparing page keys (`pageKey` in @crux/core). No model decides it, and
 * nothing a model wrote has a field that could say it (AGENTS.md §14a).
 *
 * **The questions are not the customer's.** They are `trial` prompts: asked
 * once, never in the library, never counted against the quota. A question joins
 * the library only when someone adds it to a brand.
 */
/**
 * The engines a study may ask: every engine Crux can capture. Which of them a
 * deployment asks is `CAPTURE_ENGINES`, checked by the API — a study naming
 * one it does not ask is refused, never quietly narrowed.
 */
export const PAGE_STUDY_ENGINES = [
    "chatgpt",
    "google_ai_overview",
    "gemini",
    "google_ai_mode",
    "perplexity",
    "copilot",
];
export const pageStudyEngineSchema = z.enum(PAGE_STUDY_ENGINES);
/** Five a run: each page is searched deeply, and the bill grows with every one. */
export const MAX_STUDY_PAGES = 5;
/**
 * What one capture costs, for the check before a study starts: the mean
 * DataForSEO charge across ChatGPT and AI Overview on 2026-10-08 (946
 * captures, $3.94). Gemini and AI Mode were charged $0.004 each on 2026-10-09
 * (38 live captures); Perplexity, through Oxylabs, is not measured.
 * An estimate: the API refuses a study the month's budget cannot pay for with
 * it, and the screen shows the bill with it. What is actually spent is recorded
 * per run by the capture path. The Rust API keeps its own copy
 * (`page_studies/service.rs`).
 */
export const ESTIMATED_USD_PER_CAPTURE = 0.0042;
/**
 * The most questions a deep search asks for one page, probes not counted. A
 * page usually stops sooner: when enough questions cite it, or when rounds
 * stop turning up any sign of it.
 */
export const MAX_DEEP_PROMPTS = 100;
/**
 * `fixed` writes `promptsPerPage` questions and asks each once — every study
 * before 2026-10-12, and what an old client still asks for. `deep` searches in
 * rounds, reading what each round's answers did before writing the next.
 */
export const pageStudyModeSchema = z.enum(["fixed", "deep"]);
export const createPageStudySchema = z.object({
    urls: z.array(z.string().trim().min(1).max(2_000)).min(1).max(MAX_STUDY_PAGES),
    engines: z.array(pageStudyEngineSchema).min(1).max(PAGE_STUDY_ENGINES.length),
    /**
     * Present: a fixed study of this many questions a page, the one a client
     * built before deep search priced and promised. Absent: a deep search.
     */
    promptsPerPage: z.union([z.literal(10), z.literal(20), z.literal(40)]).optional(),
    /** The market every question is asked in, e.g. `IN` and `en`. */
    country: z
        .string()
        .regex(/^[A-Za-z]{2}$/u)
        .transform((value) => value.toUpperCase()),
    language: z.string().trim().min(2).max(10),
});
/** How reading a page went. A refusal is a recorded state, not an error. */
export const pageReadStateSchema = z.enum([
    "waiting",
    "read",
    "title_only",
    "refused_by_robots",
    "blocked",
    "http_error",
    "not_html",
    "too_large",
    "unsafe_target",
    "failed",
]);
/** Where a deep search of a page stands. */
export const pageLoopStateSchema = z.enum(["searching", "confirming", "done"]);
/**
 * Why a deep search of a page stopped. `found_enough`: enough questions cite it.
 * `prompt_cap`: it asked as many as it may. `cold`: rounds stopped turning up
 * any sign of the page. `budget`: the month's capture budget could not pay for
 * the next round. `stalled`: a round's answers never all came back. `failed`:
 * the search itself broke.
 */
export const pageStopReasonSchema = z.enum([
    "found_enough",
    "prompt_cap",
    "cold",
    "budget",
    "stalled",
    "failed",
]);
/**
 * What a site-restricted probe found: whether the engine can reach the page at
 * all. `not_probed` when no probe could be built for it.
 */
export const pageReachSchema = z.enum(["reached", "not_reached", "not_probed", "failed"]);
/** How writing and asking the page's questions went. */
export const pageWriteStateSchema = z.enum([
    "waiting",
    "asked",
    "no_questions",
    "failed",
]);
/**
 * One page, and where its search is. Every count is read from the captures; a
 * page whose captures have not come back has a smaller `answered`, never a
 * smaller `cited` pretending to be final.
 */
export const studyPageSchema = z.object({
    id: z.string(),
    position: z.number().int(),
    url: z.string(),
    site: z.string(),
    title: z.string().nullable(),
    readState: pageReadStateSchema,
    writeState: pageWriteStateSchema,
    /** Why writing failed, in words a person can act on. Null unless it did. */
    writeFailure: z.string().nullable(),
    /** Questions written for this page. */
    questions: z.number().int(),
    /** Captures queued for this page, one per question per engine. */
    asked: z.number().int(),
    /** Captures that have come back, whatever their outcome. */
    answered: z.number().int(),
    /**
     * Of those, the ones that failed: no answer read, for a reason on our side
     * or the vendor's. A failed capture says nothing about the page, so a page
     * with any is never shown as "not cited" on their strength (§1).
     */
    failed: z.number().int(),
    /**
     * Of those, the ones where the engine gave no answer (`no_answer`: Google
     * showed no AI Overview, ChatGPT returned no text). An outcome, not a
     * failure — the engine cited nothing because it said nothing.
     */
    noAnswer: z.number().int(),
    /** Distinct questions whose answer cited this page. */
    cited: z.number().int(),
    /** The best place it was cited at, from one. Null when never cited. */
    bestPosition: z.number().int().nullable(),
    /** Answers that cited another page of the same site, but not this one. */
    sameSiteOther: z.number().int(),
    /** The engines whose answers cited this page. */
    citedEngines: z.array(pageStudyEngineSchema),
    /** The round a deep search is on; 0 on a fixed page and before it starts. */
    round: z.number().int(),
    /** Null on a fixed page. */
    loopState: pageLoopStateSchema.nullable(),
    stopReason: pageStopReasonSchema.nullable(),
    reach: pageReachSchema.nullable(),
});
/**
 * What one engine did with one question, for the live feed. `asking` until the
 * capture comes back; `other` when another page of the same site was cited.
 */
export const feedOutcomeSchema = z.object({
    engine: pageStudyEngineSchema,
    state: z.enum(["asking", "cited", "other", "none", "no_answer", "failed"]),
    /** Source number when cited, from one. Null otherwise. */
    position: z.number().int().nullable(),
});
/** One question in the live feed, with every engine's outcome so far. */
export const studyFeedItemSchema = z.object({
    key: z.string(),
    pageId: z.string(),
    text: z.string(),
    outcomes: z.array(feedOutcomeSchema),
});
export const pageStudySchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    engines: z.array(pageStudyEngineSchema),
    country: z.string(),
    language: z.string(),
    /** In a deep study, how many questions a round asks. */
    promptsPerPage: z.number().int(),
    mode: pageStudyModeSchema,
    /** A deep study's cap on questions a page; null on a fixed study. */
    maxPrompts: z.number().int().nullable(),
    pages: z.array(studyPageSchema),
    /** The six questions most recently asked or answered, newest first. */
    recent: z.array(studyFeedItemSchema),
});
export const pageStudySummarySchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    pages: z.number().int(),
});
export const pageStudyListSchema = z.object({ studies: z.array(pageStudySummarySchema) });
/**
 * One question that cited the page, on one engine. `runId` opens the answer
 * in the same view every other screen uses.
 */
export const pageHitSchema = z.object({
    promptId: z.string(),
    text: z.string(),
    round: z.number().int(),
    engine: pageStudyEngineSchema,
    runId: z.string(),
    /** Where the page sat among the answer's sources, from one. */
    position: z.number().int(),
    /** The sentence the page was cited behind, when the answer placed it. */
    citedSentence: z.string().nullable(),
    /** `library` once someone has added it to a brand. */
    inLibrary: z.boolean(),
    /** The question names the site the page is on ("… reddit"). */
    mentionsSite: z.boolean(),
});
export const pageHitListSchema = z.object({ hits: z.array(pageHitSchema) });
/** One page to prepare: read it, write its questions, ask them. */
export const pageStudyPageJobSchema = z.object({
    workspaceId: z.string().min(1),
    pageId: z.string().min(1),
});
//# sourceMappingURL=page-studies.js.map